// components/chat/FaixaRolavel.js — a faixa de filtros do chat ("Todas",
// "Não lidas", "Novos", "Etiquetas"…), com setas < > (out/26, redesign
// "Chat Cetto.dc.html", tela 1b).
//
// Os seis filtros ocupam ~510 px numa coluna de 360: "Pastas" e "Interesse"
// nascem fora da vista. Até aqui a única pista era um degradê FIXO na borda
// direita — que continuava lá mesmo depois de chegar ao fim, e não havia
// como voltar sem arrastar. Agora cada lado mostra degradê e seta só quando
// há o que ver naquele lado: medido no scroll, a cada render (os contadores
// e o "Etiqueta: X" mudam a largura dos chips) e quando a coluna muda de
// tamanho.
//
// As setas são atalho de mouse. Quem navega por teclado chega aos filtros
// pelo Tab e o navegador rola até o chip focado; por isso elas ficam fora
// da ordem do Tab e do leitor de tela, pra não repetir o que os chips já
// oferecem.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const reduzMovimento = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
};

export default function FaixaRolavel({ children, fundo = 'var(--ch-card)', sx }) {
  const ref = useRef(null);
  const [lados, setLados] = useState({ esq: false, dir: false });

  const medir = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // 4 px de folga: arredondamento de subpixel não acende seta à toa.
    const esq = el.scrollLeft > 4;
    const dir = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
    setLados((l) => (l.esq === esq && l.dir === dir ? l : { esq, dir }));
  }, []);

  // A cada render: o conteúdo muda de largura sem mudar o container.
  useLayoutEffect(() => { medir(); });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', medir);
      return () => window.removeEventListener('resize', medir);
    }
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [medir]);

  const rolar = (sentido) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({
      left: sentido * Math.max(140, el.clientWidth * 0.6),
      behavior: reduzMovimento() ? 'auto' : 'smooth',
    });
  };

  const seta = (sentido) => ({
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    [sentido < 0 ? 'left' : 'right']: 10,
    width: 30, height: 30, p: 0, borderRadius: '50%', cursor: 'pointer',
    border: '1px solid var(--ch-bdr2)', bgcolor: fundo, color: 'var(--ch-text)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: 'var(--ch-sh2)', zIndex: 2,
    '&:hover': { bgcolor: 'var(--ch-inset)' },
  });
  const degrade = (sentido) => ({
    position: 'absolute', top: 0, bottom: 0, pointerEvents: 'none', zIndex: 1,
    [sentido < 0 ? 'left' : 'right']: 0,
    width: sentido < 0 ? 64 : 88,
    background: `linear-gradient(to ${sentido < 0 ? 'left' : 'right'}, transparent, ${fundo} 62%)`,
  });

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <Box
        ref={ref}
        onScroll={medir}
        sx={{
          display: 'flex', gap: '6px', overflowX: 'auto', px: '16px', py: '12px',
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {children}
      </Box>
      {lados.esq && (
        <>
          <Box sx={degrade(-1)} />
          <Box component="button" type="button" tabIndex={-1} aria-hidden="true"
            title="Filtros anteriores" data-lado="esq"
            onClick={() => rolar(-1)} sx={seta(-1)}>
            <FiChevronLeft size={15} strokeWidth={2.4} />
          </Box>
        </>
      )}
      {lados.dir && (
        <>
          <Box sx={degrade(1)} />
          <Box component="button" type="button" tabIndex={-1} aria-hidden="true"
            title="Mais filtros" data-lado="dir"
            onClick={() => rolar(1)} sx={seta(1)}>
            <FiChevronRight size={15} strokeWidth={2.4} />
          </Box>
        </>
      )}
    </Box>
  );
}
